/**
 * The one decision where a refused write shows (spec I-2813 §6, §8): at the
 * fields it names, as an entry of the context's error summary, or — without a
 * context — as a persistent error toast, one per origin (K17).
 */
import type { NxToastAction } from '@blpde/nx-ui';
import { readProblem, type ProblemFieldError } from './problem';
import {
  copyRequestId,
  fieldErrorTexts,
  isSavedThenFailed,
  refusalReason,
  type ToastHost,
  type Translate,
} from './problemHelpers';
import type { Attempt, ErrorSummary, ErrorSummaryEntry, OriginKey } from './errorSummary';
import type { FieldErrorsTarget } from './fieldErrors';
import { bindsToAny } from './fieldErrorBinding';

/** The answer to a reported failure: its origin succeeded. */
export interface WriteFailureHandle {
  /** Removes the failure's entry or toast, like `resolveWriteFailure` for its origin and attempt. */
  resolve(): void;
}

/** Who failed, how the screen names it and where it may show. */
export interface ReportWriteFailureOptions {
  /** Who failed (K15): `originKey(scope, kind, id)`. */
  origin: OriginKey;
  /**
   * The attempt that failed (K14) — from `context.begin(origin)` or the
   * reporter's own token. An answer of an older attempt than the origin's
   * newest shows nothing. Without one the failure counts as the newest.
   */
  attempt?: Attempt;
  /** The caller's `useI18n().t` — the titles and actions this helper writes. */
  t: Translate;
  /** What the entry belongs to: a subsection, a grid row and column, a field label. */
  area?: string;
  /** What the write did, for the title of a `saved_then_failed` entry; default `update`. */
  kind?: 'create' | 'update';
  /**
   * `reload` closes a dialog (a create dialog, K18): the fallback detail of a
   * `saved_then_failed` create says so. Absent for a grid's create row, whose
   * reload refetches the rows and closes nothing.
   */
  closesDialog?: boolean;
  /** The screen's own phrasing of the failure; replaces the backend's detail. */
  message?: string;
  /** The inputs the surface shows: a field error that binds to one of them shows there. */
  fields?: FieldErrorsTarget;
  /** The text naming the field of a field error no input shows (K16) — a catalog label. */
  labelFor?: (fieldError: ProblemFieldError) => string | undefined;
  /**
   * A field error no input shows that the summary must not list either: the
   * surface answers it by its own rule — the untouched partner of a composite
   * key (`duplicate_value` with a `target` of the edited record), which is one
   * conflicting combination with the bound field, not a second entry.
   */
  omit?: (fieldError: ProblemFieldError) => boolean;
  /** The summary to report into; `null` or absent: none. */
  context?: ErrorSummary | null;
  /** The toast host for a failure without a context. */
  toast?: ToastHost;
  /**
   * What „Neu laden“ does for this origin (refetch, close the dialog). Offered
   * only when the write may have landed (`saved_then_failed`, K30): an
   * ordinary refusal keeps its value, a reload would discard it (rule 4).
   */
  reload?: () => void;
  /** A deliberate submit failed: the owner moves the focus to the summary (rule 10). Never for autosave. */
  focus?: boolean;
}

/** The persistent error toast of an origin (K17) — at most one per origin. */
interface OriginToast {
  id: number;
  dismiss: (id: number) => void;
  savedThenFailed: boolean;
}

/**
 * The persistent toasts of the context-less path, one per origin (K17), and
 * the newest attempt per origin a toast answered. Module-wide, because the
 * toasts outlive the component that reported them: an origin of this path
 * must therefore be unique app-wide (its scope is a reporter instance, K15).
 * A toast leaves the map when it closes (dismiss, action, resolve); the
 * attempt memory stays — a stale answer must stay stale after the toast
 * closed (K14) — and holds one number per origin that reported with an
 * attempt.
 */
const toasts = new Map<OriginKey, OriginToast>();
const newestToastAttempt = new Map<OriginKey, Attempt>();

/**
 * Shows a refused write, in this order (spec I-2813 §8):
 *
 * 1. An answer of an older attempt than the origin's newest shows nothing
 *    (K14) — unless it is `saved_then_failed`: that write may have landed, so
 *    its entry (or toast) shows and locks whichever attempt overtook it (K18,
 *    K37). Only that: the inputs belong to the newer attempt, so its field
 *    errors are neither shown nor listed, and it asks for no focus.
 * 2. The field errors that bind to `fields` show at their inputs.
 * 3. The others — all of them without `fields` — are unbound (K16), but for
 *    the ones the caller `omit`s.
 * 4. With a context: one entry with the problem's title and detail (K8), one
 *    entry per unbound field error (`area` = its label, `detail` = its own
 *    sentence); `focus` asks the owner to focus the summary. While the origin
 *    has a sticky `saved_then_failed` entry, an ordinary refusal shows at its
 *    fields only — the sticky entry and the lock stay (K31).
 * 5. Without a context, with a toast host: one persistent error toast per
 *    origin — a second refusal replaces it, but a `saved_then_failed` toast
 *    stays over a later ordinary refusal, which shows at its fields only
 *    (K31) — whose message names the unbound field errors too; its action is
 *    „Fehler-ID kopieren“ (keeps the toast), or „Neu laden“ for a
 *    `saved_then_failed` with a reload (D15).
 * 6. Neither: only the fields.
 *
 * Never a fixed sentence where the backend sent `detail` (rule 1): `message`
 * is the caller's phrasing, the error's own message only the fallback of an
 * answer without a text or of a request without an answer. An error that is
 * neither — a layer that threw on an answer it could not use, a bug — never
 * shows its message, a developer's sentence: the generic failed sentence
 * stands in and the error goes to the console. „Neu laden“ is offered only
 * when the write may have landed (K30), whatever `reload` the caller passes;
 * a screen that must offer it for another answer reports that entry itself
 * (the copy dialog's stale source, K36).
 */
export function reportWriteFailure(e: unknown, o: ReportWriteFailureOptions): WriteFailureHandle {
  const context = o.context ?? null;
  const problem = readProblem(e);
  const savedThenFailed = isSavedThenFailed(e);
  const detail =
    o.message ?? (problem?.detail || fallbackDetail(e, o, savedThenFailed));
  const title = savedThenFailed
    ? o.t(
        o.kind === 'create'
          ? 'connect.writeFailure.possiblySavedCreate'
          : 'connect.writeFailure.possiblySavedUpdate'
      )
    : problem?.title && problem.title !== detail
      ? problem.title
      : undefined;
  const reload = savedThenFailed ? o.reload : undefined;
  const fields = o.fields;
  const unbound = (problem?.fieldErrors ?? []).filter(
    (fieldError) =>
      (!fields || !bindsToAny(fieldError, fields.keys, fields.record)) && !o.omit?.(fieldError)
  );
  const showFields = () => {
    if (!fields) return;
    const texts = fieldErrorTexts(e, fields.keys, fields.record);
    if (texts.size > 0) fields.show(texts);
  };
  const label = (fieldError: ProblemFieldError) =>
    o.labelFor?.(fieldError) ?? fieldName(fieldError);

  if (context) {
    const attempt = o.attempt ?? context.begin(o.origin);
    const handle = handleFor(o.origin, attempt, context);
    const main: ErrorSummaryEntry = {
      origin: o.origin,
      attempt,
      area: o.area,
      title,
      detail,
      requestId: problem?.requestId,
      savedThenFailed: savedThenFailed || undefined,
      reload,
    };
    const outcome = context.report(main);
    // The summary ignores the answer of an older attempt (K14): nothing shows.
    if (outcome === 'ignored') return handle;
    // An overtaken possibly-saved answer: the entry and the lock, nothing else —
    // the inputs and their field errors are the newer attempt's (K37).
    if (outcome === 'overtaken') return handle;
    showFields();
    // A sticky entry of the origin stays, with the lock: this refusal shows at
    // its fields only (K31), its unbound field errors would be listed next to
    // an entry that is not theirs.
    if (outcome === 'kept') return handle;
    const used = new Set<string>();
    for (const fieldError of unbound) {
      let key = `${o.origin}|${fieldName(fieldError)}`;
      for (let n = 2; used.has(key); n += 1) key = `${o.origin}|${fieldName(fieldError)}#${n}`;
      used.add(key);
      context.report({
        origin: key as OriginKey,
        attempt,
        area: label(fieldError),
        detail: fieldError.detail || detail,
      });
    }
    if (o.focus) context.requestFocus();
    return handle;
  }

  const attempt = o.attempt;
  const handle = handleFor(o.origin, attempt, null);
  if (o.toast) {
    const newest = newestToastAttempt.get(o.origin) ?? -Infinity;
    const overtaken = attempt !== undefined && attempt < newest;
    // Stale shows nothing (K14) — but a stale saved_then_failed may have
    // landed and shows its toast anyway (K18, K37), without the fields and
    // field errors of the newer attempt; the newest attempt stays the newest.
    if (overtaken && !savedThenFailed) return handle;
    if (attempt !== undefined) newestToastAttempt.set(o.origin, Math.max(attempt, newest));
    if (!overtaken) showFields();
    const host = o.toast;
    const previous = toasts.get(o.origin);
    // The sticky toast outlives a later ordinary refusal of its origin (K31),
    // as the summary's entry does: this refusal shows at its fields only.
    if (previous?.savedThenFailed && !savedThenFailed) return handle;
    const message = [
      detail,
      ...(overtaken ? [] : unbound).map(
        (fieldError) => `${label(fieldError)}: ${fieldError.detail || detail}`
      ),
    ].join(' · ');
    if (previous) previous.dismiss(previous.id);
    const id = host.showToast({
      message,
      kind: 'error',
      persistent: true,
      action: toastAction(o, host, problem?.requestId, reload),
      onClose: () => {
        // Only the toast that is still the origin's: a host that closes the
        // replaced toast late must not forget the one that replaced it.
        if (toasts.get(o.origin)?.id === id) toasts.delete(o.origin);
      },
    });
    toasts.set(o.origin, { id, dismiss: host.dismiss, savedThenFailed });
    return handle;
  }

  showFields();
  return handle;
}

/**
 * The origin succeeded or starts again: its summary entries leave (but a
 * sticky `saved_then_failed` one), and so does its toast (but a
 * `saved_then_failed` toast, which only its own action or a dismiss closes).
 * An answer of an older attempt than the origin's newest changes nothing (K14).
 */
export function resolveWriteFailure(o: {
  origin: OriginKey;
  attempt?: Attempt;
  context?: ErrorSummary | null;
}): void {
  if (o.context) {
    o.context.resolve(o.origin, o.attempt);
    return;
  }
  if (o.attempt !== undefined) {
    if (o.attempt < (newestToastAttempt.get(o.origin) ?? -Infinity)) return;
    newestToastAttempt.set(o.origin, o.attempt);
  }
  const shown = toasts.get(o.origin);
  if (!shown || shown.savedThenFailed) return;
  // Forgotten here as well: a host that closes late must not leave it behind.
  toasts.delete(o.origin);
  shown.dismiss(shown.id);
}

/** The handle of a reported failure: resolves its origin with the attempt it answered. */
function handleFor(
  origin: OriginKey,
  attempt: Attempt | undefined,
  context: ErrorSummary | null
): WriteFailureHandle {
  return { resolve: () => resolveWriteFailure({ origin, attempt, context }) };
}

/**
 * The detail of an answer without one: the create's possibly-saved sentence
 * (plus „Dabei wird dieser Dialog geschlossen.“ when the reload closes a
 * dialog), else the error's own message — of an answer (a problem, if one
 * without a text) or of a request without one (`refusalReason`). A failure
 * that is no error at all (`null`, `undefined` — a layer that swallowed the
 * error, BLP/nx#2888) has no message to show; the failed-without-answer
 * sentence stands in, never the literal "null". An error that is neither an
 * answer nor a request without one (a layer that threw on an answer it could
 * not use, a bug) carries a developer's sentence, never a screen's (rule 1):
 * the generic failed sentence stands in, the error goes to the console.
 */
function fallbackDetail(
  e: unknown,
  o: ReportWriteFailureOptions,
  savedThenFailed: boolean
): string {
  if (savedThenFailed && o.kind === 'create') {
    const detail = o.t('connect.writeFailure.possiblySavedDetail');
    return o.closesDialog ? `${detail} ${o.t('connect.writeFailure.possiblySavedCloses')}` : detail;
  }
  if (e == null) return o.t('connect.writeFailure.noAnswer');
  const reason = refusalReason(e);
  if (reason !== undefined) return reason;
  console.error(e);
  return o.t('connect.writeFailure.failed');
}

/** The name of the field a field error blames: its key, its parameter or its target's field. */
function fieldName(fieldError: ProblemFieldError): string {
  return fieldError.key ?? fieldError.parameter ?? fieldError.target?.field ?? '';
}

/**
 * The one action of an origin's toast (D15): „Neu laden“ for a write that may
 * have been saved, when the caller can reload — it answers the error and
 * closes the toast; else „Fehler-ID kopieren“, which keeps the toast open
 * (K13) and shows the id in a second toast when the clipboard refuses (K21);
 * none when there is nothing to copy — an ordinary refusal never offers a
 * reload (K30), `reload` is already undefined for it.
 */
function toastAction(
  o: ReportWriteFailureOptions,
  host: ToastHost,
  requestId: string | undefined,
  reload: (() => void) | undefined
): NxToastAction | undefined {
  if (reload) return { label: o.t('connect.writeFailure.reload'), onClick: () => reload() };
  if (!requestId) return undefined;
  return {
    label: o.t('connect.writeFailure.copyRequestId'),
    keepOpen: true,
    onClick: () =>
      void copyRequestId(requestId).then((copied) => {
        if (copied) return;
        host.showToast({
          message: o.t('connect.writeFailure.copyFailed', { requestId }),
          kind: 'error',
          persistent: true,
        });
      }),
  };
}
